import {Box, Card, CardContent, Divider, Typography} from "@mui/material";
import {Colors} from "../../Constants/Colors.js";
import {ComponentConfig} from "../../Config/ComponentConfig.js";
import PrimaryButton from "../Common/PrimaryButton.tsx";
import React from "react";
import {Appointment} from "../../Types/index.ts";
import "../../styles/global.css";
import moment from "moment";
import {useNavigate} from "react-router-dom";
import StatusTag from "./AppointmentStatusTag.tsx";
import {Constants} from "../../Constants/Constants.js";
import {WebRoutes} from "../../Routes/WebRoutes.js";
import Api from "../../Interceptor/Api/index.ts";
import {toast} from "react-toastify";

interface AppointmentListCardProps {
    appointment: Appointment;
}

export const AppointmentListCard = ({
                                        appointment,
                                    }: AppointmentListCardProps) => {
    const [followUpLoading, setFollowUpLoading] = React.useState(false);
    const feedbackSubmitted = appointment?.feedback !== null;
    const appointmentId = appointment?.id;
    const navigate = useNavigate();
    const formattedDate = moment(appointment?.appointment_start_time).format(
        "DD MMM, YYYY"
    );
    const formattedTime = moment(appointment?.appointment_start_time).format(
        "hh:mm A"
    );
    const handleCardClick = () => {
        navigate(`/appointment/${appointmentId}`);
    };
    const appointmentStatus = appointment?.status;

    const teamNameByAppointmentStatus = {
        completed: "PX",
        upcoming: "CT",
        ongoing: "CT",
    };

    const handleFollowUp = async () => {
        try {
            setFollowUpLoading(true);
            const response = await Api.appointment.bookFollowUpAppointment(
                appointment?.id
            );
            localStorage.setItem("savedAppointment", JSON.stringify({
                ...response,
                appointment_start_time: null,
                created_at: new Date()
              }))
            // debugger
            navigate(`/book-doctor/step-3?appId=${response.appointment_id}`);
        } catch (error: any) {
            console.error("Error booking follow-up appointment:", error);
            toast.error(error?.message);
        } finally {
            setFollowUpLoading(false);
        }
    };

    return (
        <Card
            onClick={handleCardClick}
            sx={{
                width: "100%",
                margin: "auto",
                borderRadius: ComponentConfig.CardBorderRadius,
                boxShadow: 0,
                backgroundColor: Colors.appointmentCardBgColor,
                marginBottom: 1,
                padding: 0,
            }}
        >
            <CardContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    alignItems: "flex-start",
                    justifyContent: "center",
                    padding: `${ComponentConfig.cardInnerPadding} !important`,
                }}
            >
                <Box
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    {/* Patient Info */}
                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                        <Typography
                            color={Colors.primaryTextColor}
                            variant="subtitleBold"
                            className="subtitle-bold"
                        >
                            {appointment?.patient?.user_full_name}
                        </Typography>
                        <Typography
                            color={Colors.primaryTextColor}
                            variant="bodyRegular"
                            className="body-regular"
                        >
                            {appointment?.patient?.age}, {appointment?.patient?.gender}
                        </Typography>
                    </Box>

                    {/* Status Tag */}
                    <StatusTag status={appointment?.status}/>
                </Box>

                {/* Doctor Info */}
                <Typography variant="smallBold" className="small-bold">
                    Dr. {appointment?.doctor?.user_full_name}
                </Typography>

                {/* Date and Time */}
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <img
                            style={{
                                marginRight: "4px",
                                width: "16px",
                                height: "16px",
                            }}
                            src="/icons/calendar.svg"
                            alt="Appointments"
                        />
                        <Typography variant="smallRegular" className="small-regular">
                            {formattedDate}
                        </Typography>
                    </Box>

                    <Divider
                        orientation="vertical"
                        color={Colors.grey}
                        flexItem
                        sx={{mx: 1}}
                    />

                    <Box display="flex" alignItems="center">
                        <img
                            style={{
                                marginRight: "4px",
                                width: "16px",
                                height: "16px",
                            }}
                            src="/icons/clock.svg"
                            alt="Appointments"
                        />
                        <Typography variant="smallRegular" className="small-regular">
                            {formattedTime}
                        </Typography>
                    </Box>
                </Box>

                {appointmentStatus === Constants.AppointmentStatus.COMPLETED ? (
                    <>
                        {/* Follow-up Button */}
                        <PrimaryButton
                            variant="outlined"
                            disabled={followUpLoading}
                            textVariant="smallBold"
                            textClassName="small-bold"
                            text={followUpLoading ? "Loading..." :  "Book follow-up appointment"}
                            onTap={handleFollowUp}
                        />

                        {/* Share experience */}
                        {!feedbackSubmitted && (
                            <PrimaryButton
                                text={"Share your experience"}
                                sx={{
                                    padding: "0px",
                                }}
                                onTap={() => {
                                    navigate(WebRoutes.APPOINTMENT_RATING, {
                                        state: {
                                            appointmentId: appointment?.id,
                                            customerId: appointment?.patient?.id,
                                        },
                                    });
                                }}
                            />
                        )}
                    </>
                ) : appointmentStatus === Constants.AppointmentStatus.UPCOMING ||
                appointmentStatus === Constants.AppointmentStatus.ONGOING ? (
                    <PrimaryButton
                        variant="outlined"
                        textVariant="smallBold"
                        textClassName="small-bold"
                        text="Request a call"
                        onTap={() => {
                            navigate(WebRoutes.REQUEST_CALL, {
                                state: {
                                    appointmentId: appointment?.id,
                                    teamName: teamNameByAppointmentStatus[appointment?.status],
                                    customerId: appointment?.patient?.id,
                                },
                            });
                        }}
                    />
                ) : appointmentStatus === Constants.AppointmentStatus.CANCELLED ? (
                    <PrimaryButton
                        variant="outlined"
                        textVariant="smallBold"
                        textClassName="small-bold"
                        text="Reschedule appointment"
                        onTap={() => {
                            navigate("/book-doctor/step-2", {
                                state: {
                                    appointmentId: appointment?.id,
                                },
                            });
                        }}
                    />
                ) : null}
            </CardContent>
        </Card>
    );
};
